/**
 * La position de la tablette, **relevée au moment de la signature** (M20 : `lat`/`lon` sont
 * le lieu de la signature, pas celui du branchement).
 *
 * Un port, pas un appel direct à `navigator` : jsdom n'a pas de GPS, une tablette peut voir
 * sa permission refusée, et un vide sanitaire n'a pas de ciel. Rendre la capacité injectable
 * est ce qui permet d'éprouver ces trois cas au lieu de les supposer.
 */
export interface FieldPosition {
  lat: number
  lon: number
}

export interface GeolocationPort {
  /**
   * ⚠️ **Ne rejette jamais.** `null` couvre indifféremment « refusé », « indisponible » et
   * « trop long » — et dans les trois cas la conduite à tenir est la même : on transmet
   * quand même, avec un avertissement (M21). Une transmission perdue parce que le GPS n'a
   * pas accroché serait une donnée de terrain détruite.
   */
  current(): Promise<FieldPosition | null>
}

/** Le délai au-delà duquel on n'attend plus : l'agent ne doit pas rester devant un sablier. */
const TIMEOUT_MS = 10_000

export function createGeolocationPort(): GeolocationPort {
  return {
    current() {
      return new Promise((resolve) => {
        // Lu à l'appel, pas à la fabrique : la capacité peut manquer selon le contexte
        // (page non sécurisée, navigateur restreint), et une lecture au montage figerait
        // l'absence pour toute la session.
        const source: Geolocation | undefined = navigator.geolocation

        if (source === undefined) {
          resolve(null)
          return
        }

        source.getCurrentPosition(
          (position) => {
            resolve({ lat: position.coords.latitude, lon: position.coords.longitude })
          },
          () => {
            resolve(null)
          },
          { enableHighAccuracy: true, timeout: TIMEOUT_MS, maximumAge: 60_000 },
        )
      })
    },
  }
}
